import { Fragment } from 'react';
import { Chip, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { TFunction } from 'i18next';
import { Pill } from '../../../components/shared/Pill';
import { Section } from '../../../components/ui/Section';
import { applyRoundAndFormat } from '../../../utils';
import { RecipeComponent } from '../types';

export interface ComponentMetrics {
  effectiveWeight: number | null;
  effectiveCost: number | null;
  childScale: number;
}

export const getComponentRole = (component: RecipeComponent): NonNullable<RecipeComponent['componentRole']> =>
  component.componentRole ?? (component.isRecipe ? 'NestedRecipe' : 'Ingredient');

export const isFoodComponent = (component: RecipeComponent): boolean =>
  getComponentRole(component) === 'Ingredient' || getComponentRole(component) === 'NestedRecipe';

const ownWeight = (component: RecipeComponent): number | null =>
  component.weight != null && component.weight > 0 ? component.weight : component.weightNet > 0 ? component.weightNet * component.quantity : null;

const baseWeight = (component: RecipeComponent): number | null => {
  if (!component.isRecipe || !component.children?.length) return ownWeight(component);
  const childWeights = component.children
    .filter(isFoodComponent)
    .map(baseWeight)
    .filter((weight): weight is number => weight != null && weight > 0);
  const childrenTotal = childWeights.length > 0
    ? childWeights.reduce((sum, weight) => sum + weight, 0)
    : null;
  return component.weight != null && component.weight > 0 ? component.weight : childrenTotal;
};

/** Scales source-recipe child values to BOM relationship weight when declared. */
export const getComponentMetrics = (component: RecipeComponent, scale = 1): ComponentMetrics => {
  const children = component.children ?? [];
  // Nested Recipe V2 children are already scaled by the backend using the governed
  // quantity/UOM and pinned-version yield contract. Do not infer another execution
  // scale from weight ratios in the browser.
  const childScale = scale;
  const effectiveWeight = baseWeight(component);

  if (component.isRecipe && children.length > 0) {
    const childCosts = children.map(child => getComponentMetrics(child, childScale).effectiveCost);
    return {
      effectiveWeight: effectiveWeight != null ? effectiveWeight * scale : null,
      effectiveCost: childCosts.some(cost => cost != null)
        ? childCosts.reduce<number>((sum, cost) => sum + (cost ?? 0), 0)
        : null,
      childScale,
    };
  }

  return {
    effectiveWeight: effectiveWeight != null ? effectiveWeight * scale : null,
    effectiveCost: component.costPrice != null ? component.costPrice * component.quantity * scale : null,
    childScale,
  };
};

export const percentOfTotal = (effectiveWeight: number | null, totalWeight: number | null): number | null =>
  effectiveWeight != null && totalWeight != null && totalWeight > 0 ? effectiveWeight / totalWeight * 100 : null;

/**
 * Unknown component weights must not erase the useful contribution of other rows.
 * Rows with an unknown effective weight still render `—`; known rows share the
 * known rolled-up recipe weight as their denominator.
 */
export const getRecipeTotalEffectiveWeight = (components: RecipeComponent[]): number | null => {
  const knownWeights = components
    .filter(isFoodComponent)
    .map(component => getComponentMetrics(component).effectiveWeight)
    .filter((weight): weight is number => weight != null && weight > 0);

  return knownWeights.length > 0
    ? knownWeights.reduce((sum, weight) => sum + weight, 0)
    : null;
};

export const componentNumber = (siblingIndex: number): string => String(siblingIndex + 1);
const displayNumber = (value: number | null) => value == null ? '—' : value % 1 === 0 ? String(value) : value.toFixed(2);

interface ComponentRowsProps {
  keyPrefix: string;
  components: RecipeComponent[];
  numberPrefix: string;
  scale: number;
  recipeTotalWeight: number | null;
  parentEffectiveWeight?: number | null;
  expandedPaths: Set<string>;
  toggleExpanded: (path: string) => void;
  t: TFunction;
  currencyCode?: string;
}

/** Flat recursive rows. Hierarchical label and name indentation expose recipe depth. */
export const ComponentRows = ({ keyPrefix, components, numberPrefix, scale, recipeTotalWeight, parentEffectiveWeight, expandedPaths, toggleExpanded, t, currencyCode }: ComponentRowsProps) => (
  <>
    {components.map((component, index) => {
      const number = componentNumber(index);
      const hierarchyNumber = numberPrefix ? `${numberPrefix}.${number}` : number;
      const depth = hierarchyNumber.split('.').length - 1;
      const path = `${keyPrefix}:${hierarchyNumber}`;
      const hasChildren = component.isRecipe && !!component.children?.length;
      const isExpanded = expandedPaths.has(path);
      const metrics = getComponentMetrics(component, scale);
      // Root rows show their contribution to recipe total. Nested rows show their
      // contribution to their immediate sub-recipe, so each expanded level totals 100%.
      const percent = isFoodComponent(component)
        ? percentOfTotal(metrics.effectiveWeight, parentEffectiveWeight ?? recipeTotalWeight)
        : null;

      return <Fragment key={path}>
        <TableRow>
          <TableCell sx={{ width: 40, pl: 2.5 + depth * 0.5 }}>
            {hasChildren && <IconButton
              size="small"
              onClick={() => toggleExpanded(path)}
            >
              {isExpanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
            </IconButton>}
          </TableCell>
          <TableCell><Typography variant="textXS">{hierarchyNumber}</Typography></TableCell>
          <TableCell><Typography
            variant="textXS"
            fontFamily="monospace"
          >{component.componentItemNumber}</Typography></TableCell>
          <TableCell
            data-depth={depth}
            sx={{ pl: 2 + depth * 2.75 }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <span>{component.componentDescription}</span>
              <Chip
                label={t(`build.componentRoles.${getComponentRole(component)}`)}
                size="small"
                color="info"
                variant="outlined"
              />
              {hasChildren && isExpanded && <Pill
                text={String(component.children!.length)}
                sx={{ ml: 0, minWidth: 20, height: 20, p: 0, justifyContent: 'center' }}
              />}
            </Stack>
          </TableCell>
          <TableCell align="right">{component.quantity}</TableCell>
          <TableCell>{component.uom}</TableCell>
          <TableCell align="right">{displayNumber(metrics.effectiveWeight)}</TableCell>
          <TableCell align="right">{component.volume > 0 ? component.volume.toFixed(3) : '—'}</TableCell>
          <TableCell align="right">{metrics.effectiveCost != null ? applyRoundAndFormat(metrics.effectiveCost, currencyCode) : '—'}</TableCell>
          <TableCell align="right">{percent != null ? `${percent.toFixed(1)}%` : '—'}</TableCell>
        </TableRow>
        {hasChildren && isExpanded && <ComponentRows
          keyPrefix={keyPrefix}
          components={component.children!}
          numberPrefix={hierarchyNumber}
          scale={metrics.childScale}
          recipeTotalWeight={recipeTotalWeight}
          parentEffectiveWeight={metrics.effectiveWeight}
          expandedPaths={expandedPaths}
          toggleExpanded={toggleExpanded}
          t={t}
          currencyCode={currencyCode}
        />}
      </Fragment>;
    })}
  </>
);

interface RecipeComponentsTableProps {
  recipeName: string;
  components: RecipeComponent[];
  keyPrefix: string;
  expandedPaths: Set<string>;
  toggleExpanded: (path: string) => void;
  t: TFunction;
  currencyCode?: string;
}

export const RecipeComponentsTable = ({ recipeName, components, keyPrefix, expandedPaths, toggleExpanded, t, currencyCode }: RecipeComponentsTableProps) => {
  const recipeTotalWeight = getRecipeTotalEffectiveWeight(components);

  return <Section style={{ boxShadow: 'none' }}>
    <Stack
      direction="row"
      alignItems="center"
      sx={{ p: 5, gap: 1 }}
    >
      <Typography
        variant="textMD"
        fontWeight={600}
      >{recipeName} {t('build.componentsTitle')}</Typography>
      <Pill text={String(components.length)} />
    </Stack>
    <Table
      size="small"
      sx={{ '& .MuiTableCell-root:first-of-type': { pl: 2.5 }, '& .MuiTableCell-root:last-of-type': { pr: 2.5 } }}
    >
      <TableHead><TableRow>
        <TableCell sx={{ width: 40 }} /><TableCell>#</TableCell><TableCell>{t('sku')}</TableCell><TableCell>{t('name')}</TableCell>
        <TableCell align="right">{t('build.qty')}</TableCell><TableCell>{t('build.uom')}</TableCell><TableCell align="right">{t('build.weight')}</TableCell>
        <TableCell align="right">{t('build.volume')}</TableCell>
        <TableCell align="right">{t('cost')}</TableCell><TableCell align="right">{t('build.costContribution')}</TableCell>
      </TableRow></TableHead>
      <TableBody><ComponentRows
        keyPrefix={keyPrefix}
        components={components}
        numberPrefix=""
        scale={1}
        recipeTotalWeight={recipeTotalWeight}
        expandedPaths={expandedPaths}
        toggleExpanded={toggleExpanded}
        t={t}
        currencyCode={currencyCode}
      /></TableBody>
    </Table>
  </Section>;
};
